import { useUnit } from 'effector-react';
import { every } from 'lodash';
import $$ep from 'models/ep';
import navigate from 'models/router-gate';
import { $global } from 'models/store-global';

import ModalsService from 'services/ModalsService';
import RemainingStepsService from 'services/RemainingStepsService';

import useResources from 'hooks/useResources';

import { type RemainingSteps } from 'types/zod/remaining-steps';

export type ExecutionStep = {
    id: RemainingSteps;
    label: string;
    done: boolean;
    onClick?: () => void;
    to?: string;
    visible?: boolean;
};

export const useExecutionSteps = (): { steps: ExecutionStep[]; loaded: boolean; allDone: boolean } => {
    const { t } = useResources();
    const { printed, signed, tfChecked } =
        useUnit($$ep.stores.$epAnswers.map((state) => (state || {})?.hidden?.finalization)) || {};
    const loaded = useUnit($$ep.stores.$epAnswers.map(Boolean));
    const planCode = useUnit($global.map((state) => state.community?.plan));
    const isLivingTrust = planCode === 'living_trust_single' || planCode === 'living_trust_couple';

    const shipmentOptionsFeatureFlag = t('features.shipmentOptions') === true;

    const options: ExecutionStep[] = [
        {
            id: 'printed' as const,
            label: shipmentOptionsFeatureFlag
                ? t('remainingActions.options.printed.withShipment')
                : t('remainingActions.options.printed.withoutShipment'),
            done: !!printed,
            onClick: () => ModalsService.showModal('VIEW_DOCUMENTS_MODAL'),
        },
        {
            id: 'signed' as const,
            label: t('remainingActions.options.signed'),
            done: !!signed,
            onClick: () => ModalsService.showModal('SECURE_MODAL'),
        },
        {
            id: 'tfChecked' as const,
            label: t('remainingActions.options.tfChecked'),
            done: !!tfChecked,
            onClick: async () => {
                await RemainingStepsService.completeStep('tfChecked');
                navigate('/dashboard/trust-funding');
            },
            visible: isLivingTrust,
        },
    ].filter((i) => i.visible !== false);

    return {
        steps: options,
        loaded,
        allDone: every(options, 'done'),
    };
};

export default useExecutionSteps;
